import { invariant } from "@olenbetong/appframe-core";

import type { FieldDefinition } from "./fields.js";
import { indexError } from "./utils/index.js";

export const uid: unique symbol = Symbol("uid");

/**
 * Storage engine used to store data object data on the client
 */
export interface Storage<T> {
	create(record: T[]): number;
	/**
	 * Adds a record and returns its index. Without `index` the record is appended;
	 * with it the record is inserted at that position and the records after it move
	 * up one. Storage engines that predate `index` may ignore it and append.
	 */
	create(record: T | null | undefined, uid?: number | string, index?: number): number;
	destroy: (index?: number) => boolean;
	getNewUid: () => string | number;
	newRow(uid?: string | number): StorageRecord<T>;
	length: number;
	retrieve(): StorageArray<T>;
	retrieve(index: number): StorageRecord<T>;
	retrieve<F extends keyof T>(index: number, field: F): T[F] | null;
	update: (index: number, data: T) => boolean;
}

export type Nullable<T> = { [K in keyof T]: T[K] | null };
export type StorageRecord<T> = Readonly<Nullable<T> & { [uid]: number | string }>;
export type StorageArray<T> = Readonly<Array<StorageRecord<T>>>;

export class MemoryStorage<T> implements Storage<T> {
	#data: StorageArray<T> = [];
	#nextUID: number = Date.now() - 1650000000000;
	#fields: FieldDefinition[];

	constructor(fields: FieldDefinition[]) {
		this.#fields = fields;
	}

	protected fillRow = (row: Partial<T> | null, recordUid: number | string) => {
		let record = (row === null ? { [uid]: recordUid } : { [uid]: recordUid, ...row }) as Partial<StorageRecord<T>>;
		for (let field of this.#fields) {
			const prop = (field.alias ?? field.aliasName ?? field.name) as keyof T;
			if (!Object.hasOwn(record, prop)) {
				(record as Nullable<T>)[prop] = null;
			}
		}

		return record as Readonly<StorageRecord<T>>;
	};

	newRow(rowUid?: string | number) {
		return this.fillRow({}, rowUid ?? this.getNewUid());
	}

	create(record: T[]): number;
	create(record: T | null | undefined, uid?: number | string, index?: number): number;
	create(data: T[] | T | null | undefined, recordUid?: number | string, index?: number) {
		if (Array.isArray(data)) {
			let newData: StorageRecord<T>[] = new Array(data.length);
			for (let i = 0; i < data.length; i++) {
				newData[i] = this.fillRow(data[i], (data[i] as any)["PrimKey"] ?? this.getNewUid());
			}

			this.#data = newData;

			return this.#data.length - 1;
		}

		let nextUid = recordUid ?? (data as any)?.["PrimKey"] ?? this.getNewUid();
		let position = index ?? this.#data.length;
		invariant(position >= 0 && position <= this.#data.length, indexError(position));

		let newData = this.#data.slice();
		newData.splice(position, 0, this.fillRow(data ?? {}, nextUid));
		this.#data = newData;

		return position;
	}

	getNewUid() {
		return this.#nextUID++;
	}

	retrieve(): StorageArray<T>;
	retrieve(index: number): StorageRecord<T>;
	retrieve<F extends keyof T>(idx: number, field: F): StorageRecord<T>[F] | null;
	retrieve<F extends keyof T>(idx?: number, field?: F): StorageArray<T> | StorageRecord<T> | T[F] | null {
		if (idx === undefined) {
			return this.#data;
		}

		invariant(idx >= 0 && idx < this.#data.length, indexError(idx));

		let record = this.#data[idx];
		if (field) {
			return (record as T)?.[field] ?? null;
		}

		return record;
	}

	update(idx: number, data: T) {
		const current = this.#data[idx];
		invariant(idx >= 0 && idx < this.#data.length && current, indexError(idx));

		const row = this.fillRow(data, current?.[uid] ?? this.getNewUid());
		this.#data = this.#data.map((r, index) => (index === idx ? row : r));

		return true;
	}

	destroy(idx?: number) {
		if (idx === undefined || idx === null) {
			this.#data = [];
			return true;
		}

		invariant(idx >= 0 && idx < this.#data.length && this.#data[idx] !== undefined, indexError(idx));

		this.#data = this.#data.filter((_, index) => index !== idx);

		return true;
	}

	get length() {
		return this.#data.length;
	}

	set length(newLength: number) {
		if (newLength > this.#data.length) {
			let newData = this.#data.slice();
			for (let i = 0; i < newLength - this.length; i++) {
				newData.push(this.fillRow({}, this.getNewUid()));
			}

			this.#data = newData; // Object.freeze(newData);
		} else if (newLength < this.#data.length) {
			this.#data = this.#data.slice(0, newLength);
		}
	}
}
